import type { Eval3ScopedPopupValue } from './types';

/**
 * The exact Mapbox layer ids the Eval 3 (eval3-new) Master Data map renders for its
 * four "Scoped" toggles, grouped by kind. This is the single source of truth: each
 * layer component builds its `<Layer id=…>` from these (so a rename is one edit, in
 * one place), and the hover dispatcher classifies a hit-tested feature by looking its
 * `layer.id` up here. A regression test asserts every id below is classified, so a
 * layer that stops matching can no longer slip through green (INS-14081).
 *
 *   - `slot`  → `slot-layer.tsx`     (one id per well geometry: no centroid)
 *   - `ltd`   → `ltd-slot-layer.tsx` (planned locations: stick + surface point)
 *   - `tract` → `tract-layer.tsx`    (polygon fill + outline, over the `assets` tile)
 *   - `unit`  → `unit-layer.tsx`     (polygon fill + outline, over `development_areas`)
 */
export const EVAL3_SCOPED_LAYER_IDS = {
  slot: {
    stick: 'eval3-slots-stick',
    survey: 'eval3-slots-survey',
    eps: 'eval3-slots-eps',
    surfaceHole: 'eval3-slots-surface-hole',
    bottomHole: 'eval3-slots-bottom-hole',
  },
  ltd: {
    stick: 'eval3-ltd-slots-stick',
    point: 'eval3-ltd-slots-point',
  },
  tract: {
    fill: 'eval3-tracts-fill',
    line: 'eval3-tracts-line',
  },
  unit: {
    fill: 'eval3-units-fill',
    line: 'eval3-units-line',
  },
} as const;

/** The minimal shape `map.queryRenderedFeatures` gives us — layer id + props. */
interface QueriedFeature {
  layer?: { id?: string } | null;
  properties?: Record<string, unknown> | null;
}

/**
 * Reads a non-empty string MVT property.
 *
 * @param {unknown} value - The raw property value.
 * @return {string | null} The string, or null when empty / absent (MVT drops nulls).
 */
const asString = (value: unknown): string | null => {
  if (typeof value === 'string') return value.length > 0 ? value : null;
  if (typeof value === 'number') return String(value);
  return null;
};

/**
 * Reads a finite number from a numeric or numeric-string MVT property.
 *
 * @param {unknown} value - The raw property value.
 * @return {number | null} The number, or null when absent / not finite.
 */
const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Collects one deduped entry per real-world item for the features whose `layer.id` is
 * one of `layerIds`, keyed by the `idKey` property (a well draws several MVT layers and
 * a polygon draws fill + line, so the same item arrives many times). Skips a feature
 * whose identity key is absent — it cannot be addressed.
 *
 * Pass `idKey` as a `[primary, secondary]` tuple for a kind whose primary key is not
 * unique per drawn row: a Tract held in part draws one polygon per stored Subtract (its
 * own name + acres) plus the Tract outline, all under one `tract_id` and told apart only
 * by `subtract_id` (absent on the outline). Keying on `tract_id` alone would merge them
 * into a single row, so tracts dedupe on `['tract_id', 'subtract_id']`; `build` still
 * receives the primary id (the link target). INS-14081.
 *
 * @template T
 * @param {QueriedFeature[]} features - The rendered features at the hovered point.
 * @param {Record<string, string>} layerIds - The kind's layer ids (from `EVAL3_SCOPED_LAYER_IDS`).
 * @param {string | readonly [string, string]} idKey - The MVT property that identifies one
 *   item (and builds its entry), or a `[primary, secondary]` pair to also dedupe on.
 * @param {(id: string, props: Record<string, unknown>) => T} build - Builds the entry.
 * @return {T[]} The deduped entries, in first-seen order.
 */
const collect = <T>(
  features: QueriedFeature[],
  layerIds: Record<string, string>,
  idKey: string | readonly [string, string],
  build: (id: string, props: Record<string, unknown>) => T
): T[] => {
  const ids = new Set<string>(Object.values(layerIds));
  const [primaryKey, secondaryKey] =
    typeof idKey === 'string' ? [idKey, undefined] : idKey;
  const out: T[] = [];
  const seen = new Set<string>();
  for (const feature of features) {
    const layerId = feature.layer?.id;
    if (!layerId || !ids.has(layerId)) continue;
    const props = feature.properties ?? {};
    const id = asString(props[primaryKey]);
    if (!id) continue;
    const key = secondaryKey
      ? `${id}\u0000${asString(props[secondaryKey]) ?? ''}`
      : id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(build(id, props));
  }
  return out;
};

/**
 * Collects the Eval 3 scoped features under the cursor into a popup payload, reading
 * only each tile's own MVT properties (no extra fetch): slots from `wells_v2`
 * (`uwi`, `well_number`, `lease_name`, `status`), LTD places from
 * `insights_ltd_slots` (`slot_id`, `unit_name`, `formation_name`, `slot_number`),
 * tracts from `assets` (`tract_id`, `subtract_id`, `name`, `net_acreage`,
 * `net_acreage_units`), and units from `development_areas` (`name`, `unit_type`). See
 * INS-14081.
 *
 * @param {QueriedFeature[]} features - The rendered features at the hovered point.
 * @return {Eval3ScopedPopupValue | null} The grouped, deduped payload with all four
 *   groups always present (an empty group renders nothing), or null when none of the
 *   four Eval 3 scoped layers was hit (so the dispatcher falls through to the General
 *   popups and other maps are unaffected).
 */
export function extractEval3ScopedFeatures(
  features: QueriedFeature[]
): Eval3ScopedPopupValue | null {
  const result: Eval3ScopedPopupValue = {
    slots: collect(
      features,
      EVAL3_SCOPED_LAYER_IDS.slot,
      'uwi',
      (uwi, props) => ({
        uwi,
        wellNumber: asString(props.well_number),
        leaseName: asString(props.lease_name),
        status: asString(props.status),
      })
    ),
    ltdSlots: collect(
      features,
      EVAL3_SCOPED_LAYER_IDS.ltd,
      'slot_id',
      (slotId, props) => ({
        slotId,
        unitName: asString(props.unit_name),
        formationName: asString(props.formation_name),
        slotNumber: asNumber(props.slot_number),
      })
    ),
    // Keyed on the Salesforce tract id (`tract_id`), not the name: `assets` is a view
    // over `salesforce_tracts`, which has no unique index on its name, so two different
    // tracts can share one. The id is also what the row's link addresses. A Tract held in
    // part draws several rows under one `tract_id` (each stored Subtract's shape, and the
    // outline), so `subtract_id` (absent on the outline) joins the dedupe key to keep them
    // apart (INS-14081).
    tracts: collect(
      features,
      EVAL3_SCOPED_LAYER_IDS.tract,
      ['tract_id', 'subtract_id'],
      (tractId, props) => ({
        tractId,
        subtractId: asString(props.subtract_id),
        name: asString(props.name),
        netAcreage: asNumber(props.net_acreage),
        netAcreageUnits: asString(props.net_acreage_units),
      })
    ),
    units: collect(
      features,
      EVAL3_SCOPED_LAYER_IDS.unit,
      'name',
      (name, props) => ({
        name,
        unitType: asString(props.unit_type),
      })
    ),
  };

  const hasAny = (Object.values(result) as unknown[][]).some(
    (group) => group.length > 0
  );
  return hasAny ? result : null;
}
